<template>
  <div class="cc-incentive-detail-container" v-if="incentive" data-testid="incentive-detail-container">
    <div class="cc-header">
      <button class="cc-back-button" @click="$emit('back')" data-testid="back-button">
        ← {{ t('consultantCorner.Back') }}
      </button>
      <span class="cc-header-title" data-testid="program-name">{{ incentive.program.programName }}</span>
    </div>

    <div class="cc-content">
      <div class="cc-status-section">
        <div class="cc-status-card">
          <h3 data-testid="progress-header">{{ t('consultantCorner.Progress') }}</h3>
          <div class="cc-progress-container" data-testid="main-progress-container">
            <ProgressBar
              :value="calculateProgress(incentive)"
              :max="100"
              class="cc-progress-bar cc-progress-active"
            />
            <span class="cc-progress-text" data-testid="progress-percentage">{{ Math.round(calculateProgress(incentive)) }}%</span>
          </div>
        </div>

        <div class="cc-status-card">
          <h3 data-testid="time-remaining-header">{{ t('consultantCorner.TimeRemaining') }}</h3>
          <div class="cc-countdown" data-testid="countdown">
            {{ formatCountdown(incentive.daysLeft) }}
          </div>
        </div>

        <div class="cc-status-card">
          <h3 data-testid="current-level-header">{{ t('consultantCorner.CurrentLevel') }}</h3>
          <div class="cc-level" data-testid="level-name">{{ incentive.level?.levelName || '' }}</div>
        </div>
      </div>

      <div class="cc-dates-section">
        <div class="cc-date-card">
          <span class="cc-date-label" data-testid="start-date-label">{{ t('consultantCorner.StartDate') }}</span>
          <span class="cc-date-value" data-testid="start-date-value">{{ formatDate(incentive.startDate) }}</span>
        </div>
        <div class="cc-date-card">
          <span class="cc-date-label" data-testid="end-date-label">{{ t('consultantCorner.EndDate') }}</span>
          <span class="cc-date-value" data-testid="end-date-value">{{ formatDate(incentive.endDate) }}</span>
        </div>
      </div>

      <div class="cc-buckets-section">
        <div class="cc-buckets-header" data-testid="requirements-header">{{ t('consultantCorner.RequirementProgress') }}</div>
        <div class="cc-buckets-grid" data-testid="buckets-grid">
          <div v-for="bucket in incentive.buckets" :key="bucket.awardCriteria" class="cc-bucket-card" :data-testid="`bucket-card-${bucket.awardCriteria}`">
            <h3 :data-testid="`bucket-name-${bucket.awardCriteria}`">{{ bucket.bucketNameLocalized || bucket.bucketName }}</h3>
            <div class="cc-bucket-progress">
              <ProgressBar
                :value="getBucketProgress(incentive, bucket).current"
                :max="getBucketProgress(incentive, bucket).target"
                class="cc-progress-bar cc-progress-active"
                :data-testid="`bucket-progress-bar-${bucket.awardCriteria}`"
              />
              <div class="cc-bucket-values">
                <span class="cc-current-value" :data-testid="`bucket-current-${bucket.awardCriteria}`">{{ getBucketProgress(incentive, bucket).current }}</span>
                <span class="cc-target-value" :data-testid="`bucket-target-${bucket.awardCriteria}`">/ {{ getBucketProgress(incentive, bucket).target }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { ProgressBar } from '@progress/kendo-vue-progressbars'
import type { IPwsBeeIncentiveSummary } from '../../types/ConsultantCornerTypes.d'

const props = defineProps<{
  incentive: IPwsBeeIncentiveSummary
}>()

const emit = defineEmits<{
  (e: 'back'): void
}>()

const { t } = useI18n()
const logger = useLogger()

const calculateProgress = (incentive: IPwsBeeIncentiveSummary): number => {
  if (!incentive.buckets || incentive.buckets.length === 0 || !incentive.level?.compAwardStatus?.[0]?.awardStatus) {
    return 0
  }

  const awardStatus = incentive.level.compAwardStatus[0].awardStatus
  let totalCurrent = 0
  let totalTarget = 0

  incentive.buckets.forEach(bucket => {
    const award = awardStatus.find(a => a.criteriaNumber === bucket.awardCriteria)
    const current = award?.pointsEarned || 0
    const target = award?.pointsRequired || 0
    totalCurrent += current
    totalTarget += target
  })

  if (totalTarget === 0) {
    return 0
  }

  return Math.min((totalCurrent / totalTarget) * 100, 100)
}

const getBucketProgress = (incentive: IPwsBeeIncentiveSummary, bucket: IPwsBeeIncentiveSummary['buckets'][0]) => {
  if (!incentive.level?.compAwardStatus?.[0]?.awardStatus) {
    return { current: 0, target: 0 }
  }

  const award = incentive.level.compAwardStatus[0].awardStatus.find(
    a => a.criteriaNumber === bucket.awardCriteria
  )

  return {
    current: award?.pointsEarned || 0,
    target: award?.pointsRequired || 0
  }
}

const formatCountdown = (daysLeft?: number): string => {
  if (daysLeft === undefined || daysLeft === null) {
    return ''
  }

  if (daysLeft <= 0) {
    return t('consultantCorner.Closed')
  }

  return `${daysLeft} ${t('consultantCorner.DaysLeft')}`
}

const getCountdownClass = (daysLeft?: number): string => {
  if (daysLeft === undefined || daysLeft === null) {
    return ''
  }

  if (daysLeft <= 0) {
    return 'closed'
  }

  if (daysLeft <= 7) {
    return 'urgent'
  }

  if (daysLeft <= 30) {
    return 'warning'
  }

  return 'normal'
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('de-DE')
}
</script>

<style lang="scss" scoped>
.cc-incentive-detail-container {
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  max-width: 1200px;
  margin: 0 auto;
}

.cc-header {
  background-color: var(--vt-c-teal);
  color: white;
  padding: $card-padding;
  border-radius: $radius-md $radius-md 0 0;
  display: flex;
  align-items: center;
  gap: $gap-md;
}

.cc-back-button {
  background: transparent;
  border: 1px solid white;
  color: white;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-xl;
  cursor: pointer;
  transition: all 0.2s;

}

.cc-back-button:hover {
  background: rgba(white, 0.1);
}

.cc-header-title {
  @include typography-h2;
  @include font-weight-medium;
}

.cc-content {
  padding: $spacing-6;
}

.cc-status-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: $spacing-5;
  margin-bottom: 32px;
}

.cc-status-card {
  background: var(--vt-c-white-soft);
  padding: $card-padding;
  border-radius: $radius-md;

}

.cc-status-card h3 {
  @include typography-body-large;
  margin: 0 0 12px 0;
  color: #2c3e50;
}

.cc-progress-container {
  display: flex;
  align-items: center;
  gap: $spacing-3;
}

.cc-progress-bar {
  flex: 1;
  height: 8px;


}
.k-progress-status {
  display: none !important;
}

.cc-progress-text {
  @include font-weight-medium;
  color: #2c3e50;
  min-width: 48px;
  text-align: right;
}

.cc-countdown {
  @include typography-h4;
  @include font-weight-medium;

}

.cc-countdown.urgent {
  color: #dc3545;
}

.cc-countdown.warning {
  color: #ffc107;
}

.cc-countdown.normal {
  color: #28a745;
}

.cc-countdown.closed {
  color: #6c757d;
}

.cc-level {
  @include typography-h4;
  @include font-weight-medium;
  color: #2c3e50;
}

.cc-dates-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: $spacing-5;
  margin-bottom: 32px;
}

.cc-date-card {
  background: var(--vt-c-white-soft);
  padding: $spacing-4;
  border-radius: $radius-md;
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.cc-date-label {
  @include typography-body;
  color: #6c757d;
}

.cc-date-value {
  @include typography-body-large;
  @include font-weight-medium;
  color: #2c3e50;
}

.cc-buckets-section {
  h2 {
    margin: 0 0 20px 0;
    color: #2c3e50;
  }
}

.cc-buckets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: $spacing-5;
}

.cc-bucket-card {
  background: var(--vt-c-white-soft);
  padding: $card-padding;
  border-radius: $radius-md;

}

.cc-bucket-card h3 {
  @include typography-body-large;
  margin: 0 0 16px 0;
  color: #2c3e50;
}

.cc-bucket-progress {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}
.cc-buckets-header {
  @include typography-h2;
  @include font-weight-medium;
}

.cc-bucket-values {
  display: flex;
  justify-content: flex-end;
  @include typography-body;
  gap: $spacing-1;

}

.cc-bucket-values .cc-current-value {
  @include font-weight-medium;
  color: #2c3e50;
}

.cc-bucket-values .cc-target-value {
  color: #6c757d;
}

@media (max-width: 768px) {
  .cc-content {
    padding: $spacing-4;
  }

  .cc-header {
    padding: $spacing-4;
  }

  .cc-header-title {
    @include typography-h4;
  }

  .cc-status-section,
  .cc-dates-section,
  .cc-buckets-grid {
    grid-template-columns: 1fr;
  }
}
</style>
